import type { DynamicClient } from '../../../client/types/DynamicClient';
import { getCore } from '../../../client/core/getCore';

/**
 * Creates a service accessor function that manages service instantiation and caching.
 * The returned function will either retrieve an existing service from the registry or
 * create a new one using the provided builder function.
 *
 * @template - The type of service to be created/accessed
 * @param key - Unique identifier for the service in the registry
 * @param builder - Function that creates the service instance when called with a DynamicClient
 */
export const createRuntimeServiceAccessKey =
  <T>(key: string, builder: (client: DynamicClient) => T) =>
  (client: DynamicClient): T => {
    const { runtimeServices } = getCore(client);

    const currentService = runtimeServices.getByKey<T>(key);

    if (currentService) {
      return currentService;
    }

    const service = builder(client);

    runtimeServices.register(key, service);

    return service;
  };
